import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Link,
  TextField,
  Typography,
  Stack,
  useTheme,
} from "@mui/material";
import {
  useNavigate,
  Link as RouterLink,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../../features/accounts/useAuth";
import { apiUrl } from "../../services/apiClient";
import { useRecaptcha } from "../../components/forms/Recaptcha";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  loadExtUser as loadExternalUser,
  setLoginPreference,
  setPlatformGroup,
} from "../../features/accounts/auth.service";
import { ProcessButton } from "../../components/buttons/ProcessButton";
import { useAccountForm } from "./useAccountForm";
import { crossPropertyTracker } from "../../services/ga";
import { AttributionService } from "../../services/attribution.service";

type SignupData = {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  provider?: string;
  externalEmail?: string;
  inviteCode?: string;
  shopify?: string;
  urlReferrer?: string;
  signUpSource?: string;
  //abTestGroup?: string;
  abTestSource?: string;
};

const required = ["email", "password", "firstName", "lastName"];
const fields = [...required, "businessName"];

// Storage here only carries prefill hints across the OAuth redirect. Some embedded/in-app browsers
// throw SecurityError on any `localStorage` access (CM2-2NW), and that must not cost the signup.
const storageGet = (key: string): string | null => {
  try {
    return window.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const storageSet = (key: string, value: string) => {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // storage blocked: the hint is lost, the signup is not
  }
};

const Signup = () => {
  const navigate = useNavigate();
  const { doSignup } = useAuth();
  const theme = useTheme();

  const { token, isLoadingToken, loadError, regenerateToken } =
    useRecaptcha("signup");
  const [validExtUser, setValidExtUser] = useState<boolean>(false);
  const [existingAccountEmail, setExistingAccountEmail] = useState<string>();
  const [searchParams, setSearchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    data,
    setData,
    errors,
    setErrors,
    isValid,
    validateField,
    handleInputChange,
    handleSubmit,
    isProcessing,
    serverError,
  } = useAccountForm<SignupData>({
    fields,
    required: searchParams.get("ic") ? required : fields,
    validExtUser,
  });

  const externalUrl = useMemo(() => {
    const baseUrl =
      apiUrl +
      `/auth/external-login?returnUrl=` +
      encodeURIComponent(window.location.origin + "/external-login") +
      "&ic=" +
      encodeURIComponent(data?.inviteCode || "");

    // Add attribution parameters to external login URL
    const attributionParams = AttributionService.getForExternalAuth();
    return attributionParams ? `${baseUrl}&${attributionParams}` : baseUrl;
  }, [data?.inviteCode]);

  const setExtUser = useCallback(
    async (extEmail: string) => {
      const extUser = await loadExternalUser(extEmail);
      if (extUser) {
        setData({
          ...data,
          firstName: extUser.firstName,
          lastName: extUser.lastName,
          email: extEmail,
          provider: extUser.provider,
          externalEmail: extUser.emailExists ? null : extEmail,
        });

        if (extUser.emailExists) {
          // Reached by someone who clicked "Sign in with Google" and got routed here, not by someone
          // signing up: their account exists under a password. The old copy ("already in use... please
          // sign in using this email") named no way forward for the common case of not knowing the
          // password, so it read as a hard lockout. The recovery links go in an Alert above the form —
          // helperText is a plain string and cannot hold them.
          setExistingAccountEmail(extEmail);
          setErrors({
            ...errors,
            email: "This email already has an account.",
          });
        } else {
          setValidExtUser(!!extUser.firstName && !!extUser.lastName);
        }
      }
    },
    [errors, setErrors, setData, data]
  );

  useEffect(() => {
    let d = {} as SignupData;

    // Store and extract attribution parameters using the service
    const attribution = AttributionService.storeFromCurrentUrl(searchParams);

    // Apply attribution data to signup form
    if (attribution.urlReferrer) {
      d = { ...d, urlReferrer: attribution.urlReferrer };
    }
    if (attribution.signUpSource) {
      d = { ...d, signUpSource: attribution.signUpSource };
    }
    if (attribution.abTestSource) {
      d = { ...d, abTestSource: attribution.abTestSource };
    }

    // Query parameters
    if (searchParams.get("email") && !data.email) {
      d = { ...d, email: searchParams.get("email") };
    }
    if (searchParams.get("ee")) {
      const extEmail = searchParams.get("ee");
      d = { ...d, email: extEmail };
      searchParams.delete("ee");
      setSearchParams(searchParams);
      setExtUser(extEmail);
    }
    if (searchParams.get("ic") && !data.inviteCode) {
      d = { ...d, inviteCode: searchParams.get("ic") };
    }
    const org = searchParams.get("org");
    if (org) {
      d = { ...d, businessName: org };
      storageSet("businessName", org);
    }
    const shopify = searchParams.get("shopify");
    if (shopify) {
      d = { ...d, shopify };
      storageSet("shopify-store", shopify);
    }
    // Design-pack landing hint (e.g. /packs/apparel -> ?pack=apparel). Persisted so it survives the
    // Google OAuth redirect; consumed on /start to pre-set industry (see Start.tsx).
    const pack = searchParams.get("pack");
    if (pack) {
      storageSet("pack-hint", pack);
    }
    const fn = searchParams.get("fn");
    if (fn) {
      //split to first and last name
      const names = fn.split(" ");
      d = { ...d, firstName: names[0], lastName: names[1] };
      storageSet("fullName", fn);
    }

    // LocalStorage items - only apply if form field is empty AND not already set from URL params
    const storedShopify = storageGet("shopify-store");
    if (storedShopify && !d.shopify && !data.shopify) {
      d = { ...d, shopify: storedShopify };
    }
    const storedBusinessName = storageGet("businessName");
    if (storedBusinessName && !d.businessName && !data.businessName) {
      d = { ...d, businessName: storedBusinessName };
    }

    const storedFullName = storageGet("fullName");
    if (
      storedFullName &&
      !d.firstName &&
      !d.lastName &&
      !data.firstName &&
      !data.lastName
    ) {
      const names = storedFullName.split(" ");
      d = { ...d, firstName: names[0], lastName: names[1] };
    }

    // Only update if there are changes
    const hasChanges = Object.keys(d).some((key) => d[key] !== data[key]);
    if (hasChanges) {
      setData((prevData) => ({ ...prevData, ...d }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setExtUser, setSearchParams]); // Remove data and setData from dependencies

  const onSubmit = async () => {
    let currentToken = token;
    if (isLoadingToken || !currentToken) {
      currentToken = await regenerateToken();
      if (!currentToken) {
        setErrors({
          ...errors,
          email: "Unable to verify reCAPTCHA. Please try again.",
        });
        return;
      }
    }

    handleSubmit(
      "Sign up",
      async () => {
        const response = await doSignup({ ...data, token: currentToken });
        if (response.succeeded) {
          setPlatformGroup("cm2");
          setLoginPreference("cm2");

          crossPropertyTracker.trackEvent(response, "signup", {
            signup_platform: "cm2",
            signup_method: "direct",
          });

          navigate(data.inviteCode ? "/" : "/start", { replace: true });
        } else {
          await regenerateToken();
        }
        return { success: response.succeeded, message: response?.message };
      },
      async () => {
        await regenerateToken();
      }
    );
  };

  return (
    <Container sx={{ pt: 5, pb: 5 }} maxWidth="sm">
      <Box mb={5} display="flex" justifyContent="center">
        <RouterLink to="/">
          <img alt="Logo" src="/images/logo.png" />
        </RouterLink>
      </Box>

      <Card sx={{ maxHeight: "90vh", overflowY: "auto" }}>
        <CardContent sx={{ padding: 3 }}>
          <Box mb={2}>
            <Typography color="textPrimary" gutterBottom variant="h4">
              Sign up
            </Typography>
          </Box>

          {/* Social login buttons - Improved responsive layout */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Button
              size="large"
              variant="outlined"
              href={externalUrl + "&provider=Google"}
              sx={{
                justifyContent: "flex-start",
                py: 1,
                flex: { xs: "1", sm: "3" },
                whiteSpace: "nowrap",
              }}
            >
              <img
                alt="Google"
                style={{
                  marginRight: "16px",
                  width: 24,
                  height: 24,
                }}
                src="/images/google.svg"
              />
              Sign up with Google
            </Button>
          </Stack>

          {!data.provider && (
            <Box mt={3} mb={2}>
              <Typography color="textPrimary">
                or sign up with email and password:
              </Typography>
            </Box>
          )}

          {/* Suppressed on a collision: congratulating someone on authenticating and then refusing
              them in the next breath is what made this screen read as a dead end. */}
          {data.provider && !existingAccountEmail && (
            <Alert severity="success" sx={{ mt: 2 }}>
              You&quot;ve successfully authenticated with <b>{data.provider}</b>
              .{" "}
              {!data.inviteCode && (
                <span>Please enter required sign up information.</span>
              )}
            </Alert>
          )}

          {existingAccountEmail && (
            <Alert severity="info" sx={{ mt: 2 }}>
              You already have a Catalog Machine account for{" "}
              <b>{existingAccountEmail}</b>, and it signs in with a password
              rather than {data.provider || "a provider"}.{" "}
              <Link component={RouterLink} to="/login">
                Sign in with your password
              </Link>
              , or{" "}
              <Link
                component={RouterLink}
                to={`/forgot-password?email=${encodeURIComponent(
                  existingAccountEmail
                )}`}
              >
                reset it
              </Link>{" "}
              if you don&apos;t remember it. You can also sign up below with a
              different email.
            </Alert>
          )}

          {serverError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {serverError}
            </Alert>
          )}

          <Box>
            <form>
              {!validExtUser && (
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{ mt: 2 }}
                >
                  <TextField
                    error={Boolean(errors.firstName)}
                    fullWidth
                    helperText={errors.firstName}
                    label="First Name"
                    name="firstName"
                    onBlur={() => validateField("firstName")}
                    onChange={handleInputChange}
                    value={data.firstName}
                    variant="outlined"
                    required
                    inputRef={inputRef}
                    autoComplete="given-name"
                  />
                  <TextField
                    error={Boolean(errors.lastName)}
                    fullWidth
                    helperText={errors.lastName}
                    label="Last Name"
                    name="lastName"
                    onBlur={() => validateField("lastName")}
                    onChange={handleInputChange}
                    value={data.lastName}
                    variant="outlined"
                    required
                    autoComplete="family-name"
                  />
                </Stack>
              )}

              {!data.inviteCode && (
                <TextField
                  sx={{ mt: 2 }}
                  error={Boolean(errors.businessName)}
                  fullWidth
                  helperText={errors.businessName}
                  label="Business Name"
                  name="businessName"
                  onBlur={() => validateField("businessName")}
                  onChange={handleInputChange}
                  value={data.businessName}
                  variant="outlined"
                  required
                />
              )}

              {!validExtUser && (
                <TextField
                  sx={{ mt: 2 }}
                  error={Boolean(errors.email)}
                  fullWidth
                  helperText={errors.email}
                  label="Email"
                  name="email"
                  onBlur={() => validateField("email")}
                  onChange={handleInputChange}
                  type="email"
                  value={data.email}
                  variant="outlined"
                  slotProps={{
                    inputLabel: { shrink: Boolean(data.email) },
                  }}
                  autoComplete="email"
                  required
                />
              )}

              {!validExtUser && (
                <TextField
                  sx={{ mt: 2 }}
                  error={Boolean(errors.password)}
                  fullWidth
                  helperText={errors.password}
                  label="Password"
                  name="password"
                  onBlur={() => validateField("password")}
                  onChange={handleInputChange}
                  type="password"
                  value={data.password}
                  variant="outlined"
                  slotProps={{
                    inputLabel: { shrink: Boolean(data.password) },
                  }}
                  required
                />
              )}

              {loadError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  We couldn&apos;t load the security check (reCAPTCHA) needed to
                  create your account. This is usually caused by a network or
                  region that blocks Google services. Please try a different
                  network or a VPN and reload this page, or contact us in chat.
                </Alert>
              )}

              <Box mt={3}>
                <ProcessButton
                  onClick={onSubmit}
                  size="large"
                  disabled={
                    !isValid || isProcessing || isLoadingToken || loadError
                  }
                  processing={isProcessing}
                  label={data.inviteCode ? "Join Your Team" : "Create Account"}
                  color={"primary"}
                  variant="contained"
                  fullWidth
                />
              </Box>

              <Box sx={{ mt: 1, fontSize: 12 }}>
                By <strong>signing up</strong> you agree to the{" "}
                <a
                  href="https://www.catalogmachine.com/terms"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="https://www.catalogmachine.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
              </Box>
            </form>
          </Box>

          <Box mt={3} mb={1}>
            <Divider />
          </Box>

          <Box
            py={1}
            sx={{
              backgroundColor: theme.palette.background.paper,
              textAlign: "center",
            }}
          >
            <Typography variant="body1" color="textSecondary">
              Already have an account?{" "}
              <Link
                component={RouterLink}
                to={"/login"}
                variant="body1"
                color="secondary"
                sx={{ fontWeight: "medium" }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Typography
        component="div"
        sx={{
          textAlign: "center",
          mt: 2,
          color: "#999",
          fontSize: 10,
          a: { color: "#999" },
        }}
      >
        This site is protected by reCAPTCHA and the Google{" "}
        <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
        <a href="https://policies.google.com/terms">Terms of Service</a> apply.
      </Typography>
    </Container>
  );
};

export default Signup;
